import { useCallback, useEffect, useState } from 'react';

type ResourceState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: Error };

export function useResource<T>(load: () => Promise<T>, dependencies: readonly unknown[]): ResourceState<T> & {
  retry: () => void;
} {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ResourceState<T>>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });

    void load().then(
      (data) => {
        if (active) setState({ status: 'ready', data });
      },
      (reason: unknown) => {
        if (!active) return;
        const error = reason instanceof Error ? reason : new Error('The content could not be loaded.');
        setState({ status: 'error', error });
      }
    );

    return () => {
      active = false;
    };
    // The caller supplies primitive dependencies for the load function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, attempt]);

  const retry = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);

  return { ...state, retry };
}
